<template>
  <div class="stock-management-container">
    <module-navigation-bar :title="`Stock Management Lite ${langCode}`">
      <template #module-nav-actions>
        <v-tabs
          v-model="categoryTab"
          background-color="transparent"
          height="46"
          show-arrows
          style="width: auto; max-width: calc(100vw - 400px)"
        >
          <v-tab v-for="item in categories" :key="item">{{ item }}</v-tab>
        </v-tabs>
        <v-menu v-model="exportMenu" :close-on-content-click="false" offset-y>
          <template #activator="{ on, attrs }">
            <v-btn color="primary" elevation="0" dark v-bind="attrs" v-on="on">
              Export
            </v-btn>
          </template>

          <v-card min-width="300px">
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <v-menu
                    ref="startMenu"
                    v-model="rangeMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="dateRangeText"
                        label="Date range"
                        readonly
                        filled
                        v-bind="attrs"
                        prepend-inner-icon="mdi-calendar"
                        v-on="on"
                      />
                    </template>
                    <v-date-picker
                      v-model="exportDateRange"
                      color="primary"
                      range
                      @input="
                        exportDateRange.length === 2
                          ? (rangeMenu = false)
                          : null
                      "
                    />
                  </v-menu>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions>
              <v-spacer />
              <v-btn small text color="error" @click="exportMenu = false"
                >Cancel
              </v-btn>
              <v-btn small color="primary" elevation="0" @click="exportReport"
                >Download
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </template>
    </module-navigation-bar>

    <v-data-table
      v-if="focusedCategory"
      :headers="articleHeaders"
      :items="filteredArticles"
      :items-per-page="-1"
      class="full-width-table"
      sort-by="title"
      style="height: calc(100vh - 46px - 45px)"
    >
      <template #top>
        <v-toolbar dense flat>
          <v-toolbar-title>Articles</v-toolbar-title>
          <v-spacer />
          <v-text-field
            v-model="articleSearch"
            class="mr-5"
            dense
            filled
            hide-details
            placeholder="Search"
            prepend-inner-icon="mdi-magnify"
            style="max-width: 200px"
          />
          <v-btn-toggle
            v-model="btnState"
            :color="btnState === 'remove' ? 'error' : 'success'"
            group
            mandatory
          >
            <v-btn :value="'remove'" small>
              <v-icon>mdi-minus</v-icon>
              Remove
            </v-btn>
            <v-btn :value="'add'" small>
              <v-icon>mdi-plus</v-icon>
              Add
            </v-btn>
          </v-btn-toggle>
        </v-toolbar>
      </template>

      <template #item.image="{ item }">
        <v-img
          v-if="item.image?.file"
          :src="`data:${item.image.mimetype};base64,${item.image.file}`"
          max-width="100"
        />
      </template>

      <template #item.number="{ item }">
        {{ item.number }}
        <span v-if="item.supplier_number" class="fs-10">
          ({{ item.supplier_number }})
        </span>
      </template>

      <template #item.stock_counter="{ item }">
        {{ item.stock_counter }}
        <span
          v-if="stockDifference(item) !== 0"
          :style="{ color: getStockDifferenceColor(stockDifference(item)) }"
        >
          ({{ stockDifference(item) > 0 ? '+' : ''
          }}{{ stockDifference(item) }})
        </span>
      </template>

      <template #item.warning="{ item }">
        <v-tooltip v-if="item.stock_counter < item.min" right>
          <template #activator="{ on, attrs }">
            <v-icon color="error" large v-bind="attrs" v-on="on">
              mdi-alert
            </v-icon>
          </template>
          <span>Stock is below minimum value {{ item.min }}</span>
        </v-tooltip>
        <v-tooltip v-if="item.stock_counter > item.max" right>
          <template #activator="{ on, attrs }">
            <v-icon color="warning" large v-bind="attrs" v-on="on">
              mdi-alert
            </v-icon>
          </template>
          <span>Stock is above maximum value {{ item.max }}</span>
        </v-tooltip>
      </template>

      <template #item.input="{ item }">
        <v-text-field
          v-model="articleStepSize[item.id]"
          :append-icon="
            hasManualInput(item)
              ? btnState === 'remove'
                ? 'mdi-minus-circle'
                : 'mdi-plus-circle'
              : undefined
          "
          :color="btnState === 'remove' ? 'error' : 'success'"
          :disabled="loadingRows[item.id]"
          class="ml-2"
          dense
          filled
          hide-details
          style="width: 100%"
          type="number"
          @click:append="applyManualInput(item)"
          @keyup.enter="applyManualInput(item)"
        />
      </template>

      <template #item.small="{ item }">
        <v-btn
          :color="btnState === 'remove' ? 'error' : 'success'"
          :disabled="loadingRows[item.id]"
          :loading="loadingRows[item.id]"
          class="ml-5"
          elevation="0"
          width="100%"
          @click="updateStockCounter(item, item.step_small)"
        >
          <v-icon class="mr-2">
            {{ btnState === 'remove' ? 'mdi-minus' : 'mdi-plus' }}
          </v-icon>
          {{ item.step_small }}
          {{ item.step_small_title }}
        </v-btn>
      </template>

      <template #item.big="{ item }">
        <v-btn
          :color="btnState === 'remove' ? 'error' : 'success'"
          :disabled="
            loadingRows[item.id] ||
            (btnState === 'remove' && item.stock_counter < item.step_large)
          "
          :loading="loadingRows[item.id]"
          class="ml-2"
          elevation="0"
          width="100%"
          @click="updateStockCounter(item, item.step_large)"
        >
          <v-icon class="mr-2">
            {{ btnState === 'remove' ? 'mdi-minus' : 'mdi-plus' }}
          </v-icon>
          1 {{ item.step_large_title }} ({{ item.step_large }})
        </v-btn>
      </template>

      <template #item.loader="{ item }">
        <v-btn
          v-if="timerRemaining[item.id] > 0"
          :disabled="loadingRows[item.id]"
          :loading="loadingRows[item.id]"
          color="info"
          elevation="0"
          @click="forceSave(item)"
        >
          Save Now ({{ timerRemaining[item.id] }}s)
        </v-btn>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import ModuleNavigationBar from '@/components/Modules/ModuleNavigationBar.vue';
import { mapGetters } from 'vuex';
import { getRecordDocument, updateRecord } from '@/services/api/record.api';
import { queryTablesV2 } from '@/services/api/v2/connect_v2';
import { executeCustomModuleCall } from '@/services/api/module.api';
import { STOCK_MANAGEMENT_LITE } from '@/modules/modules';

export default {
  name: 'StockManagementLite',
  components: { ModuleNavigationBar },
  data: () => ({
    moduleStatus: 'loading',
    categoryTab: 0,
    categories: [],
    articles: [],
    articleTableId: null,
    articleStepSize: {},
    articleCounter: {},
    articleHeaders: [
      { text: '', value: 'image' },
      { text: 'Nr.', value: 'number' },
      { text: 'Title', value: 'title' },
      { text: '', value: 'warning' },
      { text: 'Count', value: 'stock_counter' },
      { text: '', value: 'input', width: 200, hasSort: false },
      { text: '', value: 'small', hasSort: false },
      { text: '', value: 'big', hasSort: false },
      { text: '', width: 200, value: 'loader', hasSort: false },
    ],
    btnState: 'remove',
    tableKey: 0,
    timerRemaining: {},
    loadingRows: {},
    timeouts: {},
    intervals: {},
    articleSearch: '',
    notifiedItems: new Set(),
    exportMenu: false,
    rangeMenu: false,
    exportDateRange: [],
  }),
  computed: {
    ...mapGetters(['project']),

    currentModuleRoute() {
      if (this.$route.meta && this.$route.meta.routeKey)
        return this.$route.meta.routeKey;
      const path = this.$route.path || '';
      const seg = path.split('/').filter(Boolean);
      return seg[1] || STOCK_MANAGEMENT_LITE;
    },

    moduleId() {
      const m = (this.project.modules || []).find(
        (m) => m.route === this.currentModuleRoute
      );
      return m
        ? m.id
        : (
            this.project.modules.find(
              (m) => m.route === STOCK_MANAGEMENT_LITE
            ) || {}
          ).id;
    },

    langCode() {
      if (this.$route.meta && this.$route.meta.lang)
        return this.$route.meta.lang;
      const match = this.currentModuleRoute.match(/-([A-Za-z]{2})$/);
      return match ? match[1].toUpperCase() : null;
    },
    langSuffix() {
      return this.langCode ? `_${this.langCode}` : '';
    },

    dateRangeText() {
      return this.exportDateRange.join(' ~ ');
    },
    focusedCategory() {
      return this.categories[this.categoryTab] ?? null;
    },
    stockDifference() {
      return (item) => {
        const original = item.stock_counter;
        const current = this.articleCounter[item.id] || 0;
        return current - original;
      };
    },
    filteredArticles() {
      if (this.articleSearch) {
        return this.articles.filter((a) =>
          a.title.toLowerCase().includes(this.articleSearch.toLowerCase())
        );
      }
      return this.articles;
    },
  },
  watch: {
    async focusedCategory(value) {
      if (!value) return;
      const { articles } = await queryTablesV2({
        tables: [
          {
            name: this.tn('CFFA_STOCK_ARTICLES'),
            project: this.project.id,
            as: 'articles',
            sortBy: 'number',
            columns: [
              { name: 'category', conditions: [{ operator: '=', value }] },
              ...[
                'number',
                'supplier_number',
                'title',
                'stock_counter',
                'step_small',
                'step_small_title',
                'step_large',
                'step_large_title',
                'min',
                'max',
                'delivery_time',
                'category',
                'image',
              ].map((key) => ({ name: key })),
            ],
          },
        ],
      });
      this.articles = articles.records;
      this.articleTableId = articles.id;
      this.articleStepSize = {};
      this.articles.forEach((a) => {
        this.$set(this.articleStepSize, a.id, null);
        this.articleCounter[a.id] = a.stock_counter;
        this.$set(this.loadingRows, a.id, false);
        if (a.image) {
          getRecordDocument(this.articleTableId, a.id, a.image.id).then(
            (resp) => (a.image = resp)
          );
        }
      });
    },
  },
  async mounted() {
    this.loadCategories();
  },
  methods: {
    tn(base) {
      return `${base}${this.langSuffix}`;
    },
    async loadCategories() {
      const { categories } = await queryTablesV2({
        tables: [
          {
            name: this.tn('CFFA_STOCK_ARTICLES'),
            project: this.project.id,
            as: 'categories',
            sortBy: 'category',
            columns: [{ name: 'category' }],
          },
        ],
      });
      this.categories = [...new Set(categories.records.map((x) => x.category))];
    },
    hasManualInput(item) {
      const value = parseInt(this.articleStepSize[item.id]);
      return !isNaN(value) && value !== 0;
    },
    applyManualInput(item) {
      const value = parseInt(this.articleStepSize[item.id]);
      if (isNaN(value) || value === 0) return;
      this.updateStockCounter(item, value);
      this.$set(this.articleStepSize, item.id, null);
    },
    async updateStockCounter(record, count) {
      count = parseInt(count);
      if (isNaN(count)) return;
      const newCount =
        this.btnState === 'remove'
          ? this.articleCounter[record.id] - count
          : this.articleCounter[record.id] + count;
      this.$set(this.articleCounter, record.id, newCount);
      this.tableKey++;
      clearTimeout(this.timeouts[record.id]);
      clearInterval(this.intervals[record.id]);
      this.$set(this.timerRemaining, record.id, 5);
      this.intervals[record.id] = setInterval(() => {
        if (this.timerRemaining[record.id] > 0) {
          this.$set(
            this.timerRemaining,
            record.id,
            this.timerRemaining[record.id] - 1
          );
        }
      }, 1000);
      this.timeouts[record.id] = setTimeout(async () => {
        clearInterval(this.intervals[record.id]);
        await this.saveRecord(record);
      }, 5000);
    },
    async saveRecord(record) {
      this.$set(this.loadingRows, record.id, true);
      const oldStock = record.stock_counter;
      const updatedRecord = await updateRecord(record.id, {
        project: { id: this.project.id },
        table: { id: this.articleTableId },
        record: { stock_counter: this.articleCounter[record.id] },
      });
      record.stock_counter = updatedRecord.stock_counter;
      const newStock = updatedRecord.stock_counter;
      if (
        record.min != null &&
        oldStock > record.min &&
        newStock <= record.min &&
        !this.notifiedItems.has(record.id)
      ) {
        await this.sendMinimumStockNotification(record, newStock);
        this.notifiedItems.add(record.id);
      }
      this.$set(this.loadingRows, record.id, false);
      this.$set(this.timerRemaining, record.id, null);
    },
    forceSave(record) {
      clearTimeout(this.timeouts[record.id]);
      clearInterval(this.intervals[record.id]);
      this.saveRecord(record);
    },
    getStockDifferenceColor(diff) {
      return diff > 0 ? 'green' : diff < 0 ? 'red' : '';
    },
    async sendMinimumStockNotification(item, currentStockCount) {
      try {
        const itemData = {
          number: item.number,
          title: item.title,
          stock_counter: currentStockCount,
          min: item.min,
        };
        await executeCustomModuleCall(
          this.project.id,
          this.moduleId,
          'reachedMinimum',
          { item: itemData, lang: this.langCode }
        );
      } catch (error) {
        console.error('Error sending minimum stock notification:', error);
      }
    },
    async exportReport() {
      if (this.exportDateRange.length !== 2) {
        this.$store.commit('showNotification', {
          content: 'Invalid date range',
          color: 'warning',
        });
        return;
      }
      try {
        const resp = await executeCustomModuleCall(
          this.project.id,
          this.moduleId,
          'export',
          {
            start_date: this.exportDateRange[0],
            end_date: this.exportDateRange[1],
            lang: this.langCode,
          }
        );
        if (!resp.success) {
          this.$store.commit('showNotification', {
            content: resp.message,
            color: 'error',
          });
        }
        const uri = `data:text/csv;base64,${resp.file}`;
        const link = document.createElement('a');
        link.href = uri;
        link.download = resp.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        this.exportMenu = false;
      } catch (err) {
        console.error(err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.stock-management-container {
  background-color: #f5f5f5;
}
.full-width-table {
  width: 100vw;
}
.table-wrapper {
  margin: 16px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
